import React from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, Phone, Award, FileCheck, Wrench } from 'lucide-react';
import { PHONES } from '../content';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import { SEOHead } from '../components/SEOHead';

const GUARANTEES = [
  { icon: Shield, text: 'Patlama hasarlarına karşı 24 ay garanti', color: 'var(--green)' },
  { icon: FileCheck, text: 'Çivi ve dış etkenli hasarlara karşı güvence', color: 'var(--blue)' },
  { icon: Wrench, text: 'Üretim, malzeme ve işçilik hatalarından ücretsiz tamir', color: 'var(--green)' },
  { icon: Award, text: 'Garanti kapsamındaki tamirlerden hiçbir ücret alınmaz', color: 'var(--blue)' },
  { icon: CheckCircle, text: 'Tüm marka ve modeller için geçerli', color: 'var(--green)' },
  { icon: Shield, text: 'Uzman ekibimizin sertifikalı hizmetleri', color: 'var(--blue)' },
];

export default function Garanti() {
  return (
    <main data-testid="garanti-page" style={{ background: 'var(--bg)' }}>
      <SEOHead path="/garanti" />
      <PageHero
        breadcrumb="Garanti"
        headline="24 Ay Garanti."
        accentText="Sözümüz."
        sub="Yaptığımız her işlem 24 ay garantili. Ek ücret yok, sorun olursa ücretsiz tamir."
        badge="Sertifikalı Hizmet"
      />

      {/* ─── GUARANTEE COVERAGE ─── */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3.5rem', alignItems: 'start' }} className="garanti-grid">
            {/* Left — Badge */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: .5 }}
            >
              {/* Big guarantee badge */}
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--rxl)', padding: '3rem 2rem',
                textAlign: 'center', marginBottom: '1.5rem',
              }}>
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--red) 0%, #ff4444 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 1.25rem',
                  boxShadow: '0 8px 32px rgba(229,51,51,.3)',
                }}>
                  <Shield size={36} style={{ color: '#fff' }} />
                </div>
                <div style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                  fontSize: '3.5rem', color: 'var(--blue)', lineHeight: 1, letterSpacing: '-.04em',
                }}>24</div>
                <div style={{
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                  fontSize: '1.25rem', color: 'var(--text)', marginBottom: '.35rem',
                }}>Ay Garanti</div>
                <div style={{ fontSize: '.85rem', color: 'var(--text2)' }}>
                  Tüm lastik işlemlerimiz için geçerli
                </div>
              </div>

              {/* Trust numbers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.875rem' }}>
                {[
                  { val: '7/24', label: 'Destek Hattı' },
                  { val: '%100', label: 'Müşteri Memnuniyeti' },
                ].map(({ val, label }) => (
                  <div key={label} style={{
                    background: 'var(--card)', border: '1px solid var(--border)',
                    borderRadius: 'var(--r)', padding: '1.25rem', textAlign: 'center',
                  }}>
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '1.5rem', color: 'var(--blue)', lineHeight: 1 }}>{val}</div>
                    <div style={{ fontSize: '.75rem', color: 'var(--text2)', marginTop: '.35rem' }}>{label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right — Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: .5, delay: .1 }}
            >
              <h2 style={{
                fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
                fontSize: 'clamp(1.4rem, 3vw, 1.9rem)', color: 'var(--text)',
                lineHeight: 1.15, letterSpacing: '-.025em', marginBottom: '1rem',
              }}>
                Ne <span style={{ color: 'var(--blue)' }}>Kapsıyor?</span>
              </h2>

              {/* Info box */}
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderLeft: '3px solid var(--green)', borderRadius: 'var(--r)',
                padding: '1rem 1.25rem', marginBottom: '1.5rem',
              }}>
                <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: '.25rem', fontSize: '.88rem' }}>
                  Lastiğinizde sorun mu var?
                </div>
                <p style={{ color: 'var(--text2)', fontSize: '.85rem', lineHeight: 1.65 }}>
                  Sürüş güvenliğiniz için her zaman en yeni teknolojilerle üretilen lastikleri kullanıyoruz.
                </p>
              </div>

              <p style={{ color: 'var(--text2)', lineHeight: 1.75, fontSize: '.93rem', marginBottom: '1.25rem' }}>
                Lastiklerinizde birşeylerin yanlış gittiğini düşünüyorsanız, profesyonel destek sağlayarak yardımcı olabiliriz.
              </p>

              <p style={{ color: 'var(--text2)', lineHeight: 1.75, fontSize: '.88rem', marginBottom: '1.75rem' }}>
                <strong style={{ color: 'var(--text)' }}>Lastik garantimiz</strong> ile keyfiniz artık kaçmayacak. Lastiklerimiz patlama, çivi ve dış etkenli hasarlara karşı <strong style={{ color: 'var(--blue)' }}>24 ay garantilidir.</strong> Garanti süresi içinde arızalanma durumunda işçilik dahil hiçbir ücret talep edilmez.
              </p>

              {/* Guarantee items */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem', marginBottom: '2rem' }}>
                {GUARANTEES.map(({ icon: Icon, text, color }, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: .3, delay: i * .06 }}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: '.625rem' }}
                  >
                    <Icon size={16} style={{ color, marginTop: '2px', flexShrink: 0 }} />
                    <span style={{ fontSize: '.875rem', color: 'var(--text2)', lineHeight: 1.55 }}>{text}</span>
                  </motion.div>
                ))}
              </div>

              <a
                href={PHONES.main_tel}
                data-testid="garanti-call"
                className="btn btn-primary btn-lg ring num"
                style={{ gap: '.625rem' }}
              >
                <Phone size={17} /> Garanti İçin Ara — {PHONES.main}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <FinalCTA headline="Garantili Hizmet." sub="Güvenle Ara." />

      <style>{`
        @media(max-width:768px){
          .garanti-grid{grid-template-columns:1fr!important}
        }
      `}</style>
    </main>
  );
}
